import {
  Box,
  Dialog,
  Stack,
  SwipeableDrawer,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  format,
  isSameDay,
  lastDayOfMonth,
  isAfter
} from "date-fns";
import {
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  AgendaCalendarPanel,
  AgendaCalendarRow,
  AgendaCardWrapper,
  AgendaEventsPanel,
  AgendaStack,
  AgendaVerticalDivider,
  CalendarBox
  /*HeroBox,*/
} from "./Agenda.styles";
import { AgendaEventsTabs } from "./AgendaEventsTabs.component";
import { alertNoSmartLearning } from "./utils";
import {
  // NewsCard,
  // type BadgeProps,
  CalendarCard,
  GridMain,
  SmartLearningModalDeleteContent,
  SmartLearningModalEditContent,
  WidgetDrawer,
  type DayCourse,
  type DayEvents,
  Spinner,
  Link
} from "../../components";
import { useAnnouncement } from "../../components/Announcement/Announcement.component";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { Carousel } from "../../components/Carousel_v2/Carousel.component";
import { CarouselStyle } from "../../components/Carousel_v2/Carousel.styles";
import { CarouselCardRestyle } from "../../components/CarouselCardRestyle/CarouselCardRestyle.component";
import {
  CONTENT_TYPE,
  PAGE_NAME,
  STATUS_LO
} from "../../consts";
import { UserNavigationContext } from "../../context/UserNavigationContext";
import {
  useContentQuery,
  useStructuresQueryAgenda
} from "../../queries";
import { useAuthStore, useCalendarSelectedDateStore, useLanguageStore } from "../../stores";
import type {
  CalendarDateObject,
  DayToHighlight,
  JavaBasicDate,
  LearningObject,
  MetaData,
  SmartLearningObject
} from "../../types";
import {
  calendarTimeAsDateTime,
  contentIsExpiring
} from "../../utils/calendar";
import {
  stringifyJavaBasicDate,
  toJavaBasicDate
} from "../../utils/datetime";
import { createStartDate } from "../../utils/general";

const contentTypeLabels = {
  ASYNC: "Digital",
  BLENDED: "Blended",
  SMART_LEARNING_SLOT: "Smart Learning",
  SYNC: "Live"
};

export function Agenda() {
  const { t } = useTranslation();
  const { announce } = useAnnouncement();
  const theme = useTheme();
  const location = useLocation();
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  setSpinnerLoginVisible(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const smartLearning = useAuthStore(state => state.smartConfiguration?.smartLearning);
  const widgetCalendar = useAuthStore(state => state.smartConfiguration?.widgetCalendar);
  const [showSmartCarousel, setShowSmartCarousel] = useState(false);
  const smartLearningCarousel = useRef<HTMLDivElement>(null);
  const language = useLanguageStore(state => state.language)?.id;

  //action states
  const [isOpen, setIsOpen] = useState(false);
  const [action, setAction] = useState("");
  const [actionDate, setActionDate] = useState(new Date());
  const [actionId, setActionId] = useState(0);
  const [actionStart, setActionStart] = useState("0");
  const [actionEnd, setActionEnd] = useState("0");

  //date states
  const [selectedDate, setSelectedDate] = useState(() => {
    const currentDate = new Date();
    currentDate.setHours(0,0,0);
    return currentDate;
  });
  const [selectedMonth, setSelectedMonth] = useState(parseInt(format(new Date(), "MM")));
  const [selectedYear, setSelectedYear] = useState(parseInt(format(new Date(), "yyyy")));
  const slideToSelectedDate = useCalendarSelectedDateStore((state) => state.slideTo);
  const setCalendarSelectedDate = useCalendarSelectedDateStore((state) =>
    state.setCalendarSelectedDate
  );
  const { setNavigation } = useContext(UserNavigationContext);

  useEffect(() => {
    setNavigation && setNavigation([]);
    setNavigation && setNavigation(["/agenda"]);
  }, [location.pathname]);

  useEffect(() => {
    announce( t("announcement_agenda_page"));
  }, []);

  //pageStructure
  const { data: pageStructureAgenda } = useStructuresQueryAgenda({
    pageName: PAGE_NAME.AGENDA_OVERVIEW
  });
  const{ state: routerState } = useLocation();
  //learnerMainLearningObjects
  /*const { data: main } = useContentQuery<{
    mainLearningObjects: LearningObject[],
    metadata: MetaData
  }>({
    path: pageStructureAgenda?.relativePaths?.[1]?.apiPath
  });*/
  //const main = null;


  //learnerAgenda
  const endDate = format(lastDayOfMonth(new Date(selectedYear, selectedMonth - 1, 1)), "yyyy/MM/dd");
  const selected = format(new Date(selectedDate), "yyyy/MM/dd");
  const startDate = format(new Date(selectedYear, selectedMonth -1, 1), "yyyy/MM/dd");
  const {
    data: learnerAgendaDays,
    isInitialLoading: learnerAgendaDaysAreLoading,
    isError: learnerAgendaDaysIsError
  } = useContentQuery<{
    daysToHighlight: DayToHighlight[],
    learningObjects: LearningObject[],
    smartLearningSlots: SmartLearningObject[],
    metadata: MetaData
  }>({
    path: pageStructureAgenda?.relativePaths?.[0]?.apiPath,
    queryKey: [selected, startDate, endDate],

    // additional params
    endDate: endDate.replaceAll("/","-"),
    selectedDate: selected.replaceAll("/","-"),
    startDate: startDate.replaceAll("/","-")
  });

  const dateNumbersToHighlight = learnerAgendaDays?.daysToHighlight
    // .filter((dayToHighlight) => (
    //   smartLearning === true
    //   || (dayToHighlight.learningObjects && dayToHighlight.learningObjects.length > 0)
    // ))
    // .map((dayToHighlight) => dayToHighlight.day) ?? [];
    .flatMap((dayToHighlight) => {
      const hasLearningObject = dayToHighlight.learningObjects
        ? dayToHighlight.learningObjects.length > 0
        : false;
      if (smartLearning === true || hasLearningObject) {
        return [dayToHighlight.day[2]];
      } else {
        return [];
      }
    }) ?? [];

  const learningObjects: CalendarDateObject[] =
    learnerAgendaDays?.daysToHighlight
      ?.flatMap((dayToHighlight) => (
        dayToHighlight.learningObjects
          ?.map((learningObject) => ({
            date: [selectedYear, selectedMonth, dayToHighlight.day[2]] as JavaBasicDate,
            dateObjectType: "learningObject" as const,
            endTime: learningObject.endTime, //"3 : 0"
            id: learningObject.id,
            learningObject,
            // learningObject: {
            //   ...learningObject,
            //   empty: false
            // },
            learningObjectType: learningObject.learningObjectType,
            smartLearning: null,
            startTime: learningObject.startTime, //"2 : 0"
            title: learningObject.title,
            sessionDuration: learningObject.sessionDuration,
            startDateTime: learningObject.startDateTime,
            endDateTime: learningObject.endDateTime
          } satisfies CalendarDateObject)
          ) ?? []
      )) ?? [];

  const smartLearnings: CalendarDateObject[] = smartLearning ? (
    learnerAgendaDays?.daysToHighlight
      ?.flatMap((dayToHighlight) => (
        dayToHighlight?.smartLearningSlots
          ?.map((smartLearning) => ({
            date: [selectedYear, selectedMonth, dayToHighlight.day[2]] as JavaBasicDate,
            dateObjectType: "smartLearning" as const,
            endTime: smartLearning.endTime, //"3 : 0"
            id: smartLearning.id,
            learningObject: null,
            learningObjectType: CONTENT_TYPE.SMART_LEARNING_SLOT,
            smartLearning,
            startTime: smartLearning.startTime, //"2 : 0"
            title: contentTypeLabels[CONTENT_TYPE.SMART_LEARNING_SLOT]
          } satisfies CalendarDateObject)
          ) ?? []
      )) ?? []
  ) : [];

  const dateObjects =
      learningObjects?.concat(smartLearnings)
        .sort((dateObject1, dateObject2) => {
          if (
            dateObject1.dateObjectType === "learningObject"
            && dateObject2.dateObjectType === "smartLearning"
          ) {
            return -1;
          } else if (
            dateObject1.dateObjectType === "smartLearning"
            && dateObject2.dateObjectType === "learningObject"
          ) {
            return 1;
          } else if (
            dateObject1.dateObjectType === "learningObject"
            && dateObject2.dateObjectType === "learningObject"
          ) {
            const courseAIsExpiring = contentIsExpiring(dateObject1.learningObject, selectedDate);
            const courseBIsExpiring = contentIsExpiring(dateObject2.learningObject, selectedDate);

            if (courseAIsExpiring && !courseBIsExpiring) {
              return -1;
            } else if (!courseAIsExpiring && courseBIsExpiring) {
              return 1;
            } else { // same value
              return 0;
            }
          } else if (
            dateObject1.dateObjectType === "smartLearning"
            && dateObject2.dateObjectType === "smartLearning"
          ) {
            if (dateObject1.smartLearning.startTime < dateObject2.smartLearning.startTime) {
              return -1;
            } else if (dateObject1.smartLearning.startTime > dateObject1.smartLearning.startTime) {
              return 1;
            } else {
              return 0;
            }
          } else {
            return 0;
          }
        });

  const calendarCarouselCards = buildCarouselCards();
  const liveCarouselCards = buildCarouselCards((obj) =>
    obj.learningObjectType === CONTENT_TYPE.SYNC
  );
  const mandatoryCarouselCards = buildCarouselCards((obj) =>
    obj.dateObjectType === "learningObject" && obj.learningObject?.isMandatory === true
  );
  const smartCarouselCards = buildCarouselCards((obj) =>
    obj.learningObjectType === CONTENT_TYPE.SMART_LEARNING_SLOT
  );

  const firstSmartCourse = smartLearnings.find((smartLearning) => (
    smartLearning.dateObjectType !== "empty"
      && !isAfter(
        new Date(),
        calendarTimeAsDateTime({
          date: smartLearning.date,
          time: smartLearning.startTime
        })
      )
  ));//?.id as number | undefined;
  const firstSmartCourseEndTime =
    firstSmartCourse
      ? calendarTimeAsDateTime({
        date: firstSmartCourse?.date,
        time: firstSmartCourse?.endTime
      })
      : null;
  const firtstSmartCourseStartTime =
    firstSmartCourse
      ? calendarTimeAsDateTime({
        date: firstSmartCourse?.date,
        time: firstSmartCourse?.startTime
      })
      : null;

  //learnersSmartLearningObjects
  const {
    data: smart,
    isLoading: smartIsLoading,
    isError: smartIsError
  } = useContentQuery<{
      smartLearningObjects: LearningObject[]
      metadata: MetaData
    }>({
      path: pageStructureAgenda?.relativePaths?.[4]?.apiPath,

      endTime: format(new Date(firstSmartCourseEndTime as Date),  "yyyy-MM-dd'T'hh:mm:ss.sss'Z'"),
      startTime: format(new Date(firtstSmartCourseStartTime as Date), "yyyy-MM-dd'T'hh:mm:ss.sss'Z'")
    });

  useEffect(() => {
    const slideToIndex = calendarCarouselCards
      .findIndex((calendarCarouselCard) => (
        calendarCarouselCard.find((event) => {
          const eventDate = new Date(event.date.join("/"));
          return isSameDay(eventDate, selectedDate);
        })
      ));
    const slideTo = slideToIndex !== -1
      ? slideToIndex
      : slideToSelectedDate;
    setCalendarSelectedDate({ selectedDate, slideTo });
  }, [
    selectedDate,
    setCalendarSelectedDate,
    slideToSelectedDate,
    calendarCarouselCards
  ]);

  useEffect(() => {
    if (!smartIsLoading && showSmartCarousel) {
      scrollToSmartLearning();
    }
  }, [showSmartCarousel, smartIsLoading]);

  useEffect(() => {
    setShowSmartCarousel(false);
  }, [selectedDate]);

  function handleScrollAction() {
    if (!showSmartCarousel) {
      setShowSmartCarousel(true);
    } else {
      scrollToSmartLearning();
    }
  }

  const agendaTabs = [
    { id: "all",           label: t("agenda_tab_all"),            cards: calendarCarouselCards },
    { id: "live",          label: t("agenda_tab_live"),           cards: liveCarouselCards },
    { id: "mandatory",     label: t("agenda_tab_mandatory"),      cards: mandatoryCarouselCards },
    { id: "smartLearning", label: t("agenda_tab_smart_learning"), cards: smartCarouselCards }
  ];

  return widgetCalendar === true ? (
    <AgendaStack>
      <GridMain>
        <WidgetDrawer/>

        <Box sx={ { width: "100%" } }>
          <AgendaCardWrapper>
            <AgendaCalendarRow>
              <AgendaCalendarPanel role="region" aria-label={ t("calendar") }>
                <CalendarBox tabIndex={ -1 }>
                  <CalendarCard
                    dateObjects={ dateObjects }
                    dateNumbersToHighlight={ dateNumbersToHighlight ?? [] }
                    selectedExternal={ selectedDate ?? new Date() }
                    setSelectedExternal={ setSelectedDate }
                    setSelectedMonth={ setSelectedMonth }
                    setSelectedYear={ setSelectedYear }
                  />
                </CalendarBox>
              </AgendaCalendarPanel>

              <AgendaVerticalDivider />

              <AgendaEventsPanel>
                { !learnerAgendaDaysIsError && (
                  <>
                    <Typography 
                      fontWeight={ "bold" } 
                      fontSize={ "1.25rem" }
                      marginLeft="0.25rem"
                    >
                      { t("upcoming_events") }
                    </Typography>
                    <AgendaEventsTabs
                      tabs={ agendaTabs }
                      isLoading={ learnerAgendaDaysAreLoading || learnerAgendaDays === undefined }
                      firstSmartCourseId={ firstSmartCourse?.id ?? undefined }
                      selectAction={ selectAction }
                      scrollAction={ handleScrollAction }
                      setShowSmartLearning={ () => setShowSmartCarousel(true) }
                    />
                  </>
                ) }
              </AgendaEventsPanel>
            </AgendaCalendarRow>
          </AgendaCardWrapper>
        </Box>
        {
          smartIsError ? null : smartIsLoading ? (
            <Spinner />
          ) : smart && showSmartCarousel && Array.isArray(smart.smartLearningObjects) ? (
            (
              <CarouselStyle 
                aria-label={ t("smart_learning") }
                className="carouselContainer"
                marginTop={ "1.5rem" }
              >
                <>
                  <Box sx={ {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  } }>
                    <Stack flexDirection={ "row" } gap={ "2rem" } alignItems={ "center" }>
                      <Typography
                        tabIndex={ 0 }
                        variant="h2"
                        fontSize={ "1.5rem" }
                        fontWeight={ 700 }
                        lineHeight={ "2.1rem" }
                        aria-label={ t("carousel_title_aria_label",{ name:t("smart_learning") }) }
                      >
                        { t("smart_learning") }
                      </Typography>

                    </Stack>
                    <Link
                      aria-label={
                        t("discover_all_aria_label",
                          {
                            number_of_items: smart.metadata.totalElements,
                            carousel_name: t("smart_learning")
                          })
                      }
                      href={
                        `/smart-learning?endTime=${firstSmartCourseEndTime?.toISOString()}&startTime=${firtstSmartCourseStartTime?.toISOString()}`
                      }
                      state={ {
                        ...routerState,
                        titleCards: t("smart_learning")
                      } }
                    >
                      <ButtonDigited
                        tabIndex={ -1 }
                        disabled={ smartIsLoading }
                        typology="tertiary"
                        buttonSize="md"
                        text={ `${t("discover_all")}  (${smart.metadata.totalElements})` }
                      ></ButtonDigited>
                    </Link>
                  </Box>

                  { alertNoSmartLearning(smart.smartLearningObjects, theme, t) }

                </>
                { smart.smartLearningObjects.length > 0 && (
                  <Carousel
                    centeredSlides={ isMobile ? true : false }
                    carouselData={ smart.smartLearningObjects as LearningObject[] ?? [] }
                    spaceBetween={ 16 }
                    loopSlides={ false }
                    hasPagination={ false }
                    hideSweeperBulletsRight={ true }
                    hasPaginationSweeperBullets={ false }
                    title={ t("smart_learning") }
                    pauseOnMouseEnter={ true }
                    autoplay={ false }
                    renderElement={ (card) => {
                      return (
                        <CarouselCardRestyle
                          booked={ card.status !== STATUS_LO.N && card.status !== STATUS_LO.T }
                          categories={ card.topicTags }
                          coverPublicURL={ card.coverPublicURL?.toString() || "" }
                          coverVideoPublicURL={ card.videoPublicURL }
                          disabled={ false }
                          duration={ card.duration }
                          ecm_specialization={ card.ecm_specialization }
                          ecmRegistration={ card.ecmRegistration }
                          editionNumber={ card?.editionNumber }
                          endDate={ createStartDate(card) }
                          enrollType={ card.enrollType }
                          expirationDate={ card.expirationDate }
                          id={ card.id }
                          isAutoSubscribeEnable={ card.isAutoSubscribeEnable }
                          isFavourite={ card.isFavourite }
                          isMandatory={ card.isMandatory }
                          isSurvey={ card.isSurvey }
                          isTest={ card.isTest }
                          isToj={ card.isToj }
                          key={ `smart-card-${card.id}--${language}` }
                          learningObjectType={ card.learningObjectType }
                          learningObjectTypology={ card.learningObjectTypology }
                          percentageOfCompletion={ card.percentageOfCompletion }
                          shortDescription={ card.shortDescription }
                          startDate={ createStartDate(card)  }
                          status={ card.status }
                          title={ card.title }
                          cardType="small"
                          canAccessNow={ card.canAccessNow }
                          isLinkRelative={ card.isLinkRelative }
                          link={ card.link }
                          isCarouselView={ true }

                        />
                      );
                    }
                    }
                  />
                ) }
              </CarouselStyle>
            )

          ) : null
        }
      </GridMain>
      { action.length > 0 &&
        <>
          { isMobile ?
            <SwipeableDrawer
              anchor={ "bottom" }
              open={ isOpen }
              disablePortal={ true }
              onClose={ () => setIsOpen(false) }
              onOpen={ () => setIsOpen(true) }
              className={ "smart-drawer" }
              sx={ {
                "& .MuiModal-backdrop": {
                  bottom: "60px"
                },

                "& .MuiPaper-root" : {
                  background: theme.linearGradients.backgroundCard,
                  border: `2px solid ${theme.customColors.border}`,
                  borderColor: theme.customColors.borderAccent,
                  borderRadius: "8px 8px 0 0",
                  bottom: "60px",
                  padding: "16px 20px",
                  transition: "none",

                  [theme.breakpoints.down("sm")]:{
                    height: "calc(100% - 120px)"
                  }
                }
              } }
            >
              { action == "modify" ?
                <SmartLearningModalEditContent
                  onClose={ () => setIsOpen(false) }
                  selectedDate={ actionDate }
                  slotId={ actionId }
                  start={ actionStart }
                  end={ actionEnd }
                /> :
                <SmartLearningModalDeleteContent
                  onClose={ () => setIsOpen(false) }
                  slotId={ actionId }
                />
              }
            </SwipeableDrawer> :
            <Dialog
              maxWidth={ false }
              open={ isOpen }
              onClose={ (event, reason) => {
                if(reason == "backdropClick" || reason == "escapeKeyDown") {
                  return;
                }
                setIsOpen(false);
              } }
              scroll={ "paper" }
              sx={ {
                ".MuiDialog-paper ": {
                  background: theme.customColors.backgroundCard,
                  border: `2px solid ${theme.customColors.border}`,
                  borderRadius:"4px",
                  bottom: "10%",
                  //maxHeight:"calc(100% - 32px)",
                  padding: "24px 40px",
                  width: "494px",
                  "&:hover": {
                    border: `2px solid ${theme.customColors.borderAccent}`
                  }
                }
              } }
            >
              { action == "modify" ?
                <SmartLearningModalEditContent
                  onClose={ () => setIsOpen(false) }
                  selectedDate={ actionDate }
                  slotId={ actionId }
                  start={ actionStart }
                  end={ actionEnd }
                /> :
                <SmartLearningModalDeleteContent
                  onClose={ () => setIsOpen(false) }
                  slotId={ actionId }
                />
              }
            </Dialog>
          }
        </>
      }
    </AgendaStack>
  ) : (
    <GridMain
      justifyContent={ "center" }
    >
      <Typography
        padding={ "42px 80px" }
        textAlign={ "center" }
      >
        { t("no_agenda_available") }
      </Typography>
    </GridMain>
  );

  function scrollToSmartLearning() {
    const offsetTop = smartLearningCarousel?.current?.offsetTop ?? 1000;
    const scrollY = offsetTop - 80 - 42; //to make up for the carousel's title height + margin
    document.scrollingElement?.scrollTo({ behavior: "smooth", top: scrollY });
  }

  function getCalendarCards({
    events
  } : {
    events: CalendarDateObject[]
  }) {
    const eventPages: DayEvents[][] = [];
    for (let i = 0; i < events.length; i = i + 3) {
      let eventsToShow = events
        .slice(i, (i + 3))
        .map((event) => {
          const courseObj: DayCourse =
            event.dateObjectType === "learningObject" ? {
              date: event.date,
              dayCourseType: event.dateObjectType,
              duration: event.learningObject.duration ? parseInt(event.learningObject.duration) : 0,
              endTime: event.endTime,
              enrollType: event.learningObject.enrollType,
              expirationDate: event.learningObject.expirationDate,
              grandParentId: event.learningObject.grandParentId ?? undefined,
              id: event.id,
              isMandatory: event.learningObject.isMandatory,
              learningObjectType: event.learningObjectType,
              learningObjectTypology: event.learningObject.learningObjectTypology,
              parentId: event.learningObject.parentId ?? undefined,
              percentageOfCompletion: event.learningObject.percentageOfCompletion ?? "0%",
              startTime: event.startTime,
              status: event.learningObject.status,
              title: event.title,
              sessionDuration: event.sessionDuration || 0,
              startDateTime: event.startDateTime,
              endDateTime: event.endDateTime,
              stakeholders: event.learningObject.stakeholders,
              sessions: event.learningObject.sessions
            } : event.dateObjectType === "smartLearning" ? {
              date: event.date,
              dayCourseType: event.dateObjectType,
              endTime: event.endTime,
              enrollType: null,
              expirationDate: null,
              id: event.id,
              isMandatory: null,
              learningObjectType: event.learningObjectType,
              learningObjectTypology: null,
              percentageOfCompletion: null,
              startTime: event.startTime,
              status: null,
              title: null,
              startDateTime: event.startDateTime,
              endDateTime: event.endDateTime
            } : {
              date: event.date,
              dayCourseType: event.dateObjectType,
              startDateTime: event.startDateTime,
              endDateTime: event.endDateTime
            };
          return courseObj;
        });

      const currentPage: DayEvents[] = [];
      eventsToShow.forEach((eventToShow) => {
        if (eventToShow.dayCourseType === "empty") {
          currentPage.push({
            courses: [],
            date: eventToShow?.date
          });
        } else if (eventToShow.learningObjectType === CONTENT_TYPE.SMART_LEARNING_SLOT) {
          currentPage.push({
            courses: [eventToShow],
            date: eventToShow?.date
          });
        } else {
          const existingEventDate = currentPage
            .find((event) => (
              event?.date.join("/") === eventToShow?.date.join("/")
            ));

          if (existingEventDate) {
            existingEventDate.courses.push(eventToShow);
          } else {
            currentPage.push({
              courses: [eventToShow],
              date: eventToShow?.date
            });
          }
        }
      });
      eventPages.push(currentPage);
    }
    return eventPages;
  }

  function buildCarouselCards(filter?: (obj: CalendarDateObject) => boolean) {
    let items = dateObjects.filter((dateObject) => {
      const dateObjectDate = new Date(dateObject.date.join("/"));
      return isSameDay(dateObjectDate, selectedDate) || isAfter(dateObjectDate, selectedDate);
    });

    if (filter) {
      items = items.filter(filter);
    }

    const hasSelectedDay = items.some((item) => {
      const itemDate = new Date(item.date.join("/"));
      return isSameDay(itemDate, selectedDate);
    });

    const withEmpty: CalendarDateObject[] = !learnerAgendaDaysAreLoading && !hasSelectedDay
      ? [
        {
          date: toJavaBasicDate(selectedDate),
          dateObjectType: "empty" as const,
          endTime: null,
          id: null,
          learningObject: null,
          learningObjectType: null,
          smartLearning: null,
          startTime: null,
          title: null
        },
        ...items
      ]
      : items;

    withEmpty.sort((cdo1, cdo2) =>
      stringifyJavaBasicDate(cdo1.date) >= stringifyJavaBasicDate(cdo2.date) ? 1 : -1
    );

    return getCalendarCards({ events: withEmpty });
  }

  function selectAction(
    action: string,
    date: Date,
    slotId: number,
    start: string,
    end: string
  ) {
    setAction(action);
    setIsOpen(true);
    setActionDate(date);
    setActionId(slotId);
    setActionStart(start);
    setActionEnd(end);
  }
}
